import { useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type * as S from "@shipyard/shared";
import { CLOUD_PLAN_FEATURES, CLOUD_PLAN_LIMITS, CLOUD_PLANS, type CloudPlanName } from "@shipyard/shared/constants";
import { toastError } from "@/components/common";
import { Badge, Button, Card, CardHeader, PageHeader, Spinner } from "@/components/ui";
import { http } from "@/lib/api";
import { keys, useBilling, useMe } from "@/lib/queries";
import { cn, formatBytes, formatPrice } from "@/lib/utils";
import { licenseDate } from "./admin";

function Meter({ label, used, limit, format = String }: { label: string; used: number; limit: number | null; format?: (n: number) => string }) {
  const { t } = useTranslation();
  const ratio = limit ? Math.min(1, used / limit) : 0;
  return (
    <div>
      <div className="flex justify-between text-sm">
        <span className="text-fg-muted">{label}</span>
        <span className="font-medium">{limit === null ? t("billing.unlimitedUsage", { used: format(used) }) : `${format(used)} / ${format(limit)}`}</span>
      </div>
      {limit !== null && (
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted" role="meter" aria-label={label} aria-valuenow={used} aria-valuemax={limit}>
          <div className={cn("h-full rounded-full", ratio >= 1 ? "bg-red-500" : ratio >= 0.8 ? "bg-amber-500" : "bg-brand-600")} style={{ width: `${ratio * 100}%` }} />
        </div>
      )}
    </div>
  );
}

function PlanCard({ plan, billing, owner }: { plan: CloudPlanName; billing: S.Billing; owner: boolean }) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState<S.BillingInterval | null>(null);
  const limits = CLOUD_PLAN_LIMITS[plan];
  const current = billing.plan === plan;
  const offers = billing.offers.filter((o) => o.plan === plan);
  const intervals = offers.map((o) => o.interval);
  const unlimited = t("billing.unlimited");
  const lines = [
    limits.seats === null ? t("billing.seatsPaid") : t("billing.seatsIncluded", { count: limits.seats }),
    t("billing.apps", { value: limits.apps ?? unlimited }),
    t("billing.builds", { value: limits.buildsPerMonth ?? unlimited }),
    t("billing.storage", { value: limits.storageBytes === null ? unlimited : formatBytes(limits.storageBytes) }),
    ...CLOUD_PLAN_FEATURES[plan].map((f) => t(`license.feature.${f}`)),
  ];
  const subscribe = async (interval: S.BillingInterval) => {
    setBusy(interval);
    try {
      const { url } = await http.post<{ url: string }>("/org/billing/checkout", { plan, interval });
      window.location.assign(url);
    } catch (err) {
      toastError(err);
      setBusy(null);
    }
  };
  return (
    <Card className={cn("flex flex-col p-5", current && "ring-2 ring-brand-600")}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{t(`license.plan.${plan}`)}</h3>
        {current && <Badge tone="blue">{t("billing.current")}</Badge>}
      </div>
      <p className="mt-1 text-sm text-fg-muted">{t(`billing.tagline.${plan}`)}</p>
      {offers.map(
        ({ interval, unitAmount, currency }) =>
          unitAmount !== null &&
          currency && (
            <p key={interval} className="mt-1 text-sm font-medium">
              {t(interval === "month" ? "billing.pricePerSeatMonth" : "billing.pricePerSeatYear", { price: formatPrice(unitAmount, currency, i18n.language) })}
            </p>
          ),
      )}
      <ul className="mt-4 flex-1 space-y-1.5 text-sm">
        {lines.map((line) => (
          <li key={line} className="flex gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
            {line}
          </li>
        ))}
      </ul>
      {plan !== "free" && billing.plan === "free" && owner && intervals.length > 0 && (
        <div className="mt-5 flex flex-col gap-2">
          {intervals.map((interval) => (
            <Button key={interval} variant={interval === "month" ? "primary" : "outline"} loading={busy === interval} disabled={!!busy} onClick={() => subscribe(interval)}>
              {t(interval === "month" ? "billing.subscribeMonthly" : "billing.subscribeYearly", { plan: t(`license.plan.${plan}`) })}
            </Button>
          ))}
        </div>
      )}
    </Card>
  );
}

export function BillingPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const me = useMe();
  const billing = useBilling();
  const search = useSearch({ strict: false }) as { checkout?: string };
  const [portalBusy, setPortalBusy] = useState(false);
  const owner = me.data?.organization?.role === "owner";

  useEffect(() => {
    if (search.checkout === "success") {
      toast.success(t("billing.checkoutSuccess"));
      // The subscription arrives through the Stripe webhook, usually within seconds.
      const timer = setTimeout(() => void qc.invalidateQueries({ queryKey: keys.billing }), 3000);
      return () => clearTimeout(timer);
    }
  }, [search.checkout, qc, t]);

  if (!billing.data) return <Spinner />;
  const data = billing.data;
  const paid = data.plan !== "free";

  return (
    <>
      <PageHeader
        title={t("billing.title")}
        description={t("billing.help")}
        actions={
          paid &&
          owner && (
            <Button
              variant="outline"
              loading={portalBusy}
              onClick={async () => {
                setPortalBusy(true);
                try {
                  window.location.assign((await http.post<{ url: string }>("/org/billing/portal")).url);
                } catch (err) {
                  toastError(err);
                  setPortalBusy(false);
                }
              }}
            >
              {t("billing.manage")}
            </Button>
          )
        }
      />
      <div className="space-y-6">
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                {t(`license.plan.${data.plan}`)}
                {data.status === "past_due" && <Badge tone="red">{t("billing.pastDue")}</Badge>}
              </span>
            }
            description={
              data.currentPeriodEnd && paid
                ? t(data.cancelAtPeriodEnd ? "billing.endsOn" : "billing.renewsOn", { date: licenseDate(data.currentPeriodEnd, i18n.language) })
                : t("billing.freeHelp")
            }
          />
          <div className="grid gap-5 p-5 sm:grid-cols-2">
            <Meter label={t("billing.seatsUsage")} used={data.seats.used} limit={data.seats.limit ?? data.seats.purchased} />
            <Meter label={t("billing.appsUsage")} used={data.usage.apps} limit={data.limits.apps} />
            <Meter label={t("billing.buildsUsage")} used={data.usage.buildsThisMonth} limit={data.limits.buildsPerMonth} />
            <Meter label={t("billing.storageUsage")} used={data.usage.storageBytes} limit={data.limits.storageBytes} format={formatBytes} />
          </div>
        </Card>
        {!owner && <p className="text-sm text-fg-muted">{t("billing.ownersOnly")}</p>}
        <div className="grid gap-4 md:grid-cols-3">
          {CLOUD_PLANS.map((plan) => (
            <PlanCard key={plan} plan={plan} billing={data} owner={owner} />
          ))}
        </div>
        <p className="text-xs text-fg-muted">{t("billing.seatsExplained")}</p>
      </div>
    </>
  );
}
